import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GraduationCap, CheckCircle2, XCircle, Mail } from 'lucide-react';
import authService from '../../services/api/auth';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const VerifyEmail = () => {
  const { token } = useParams();
  const toast = useToast();
  const { updateUser } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const hasRunRef = useRef(false);

  useEffect(() => {
    // منع تنفيذ الطلب أكثر من مرة (خاصة في وضع التطوير مع React StrictMode)
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('رابط التفعيل غير صالح.');
        return;
      }

      try {
        const res = await authService.verifyEmail(token);
        setStatus('success');
        setMessage(res?.message || 'تم تفعيل البريد الإلكتروني بنجاح.');
        toast.success('تم تفعيل بريدك الإلكتروني بنجاح.');

        // تحديث بيانات المستخدم في حال كان مسجلاً دخوله بالفعل
        try {
          const profileRes = await authService.getProfile();
          if (profileRes?.user) {
            updateUser(profileRes.user);
            const storedUser = localStorage.getItem('user');
            const parsedStored = storedUser ? JSON.parse(storedUser) : {};
            localStorage.setItem('user', JSON.stringify({ ...parsedStored, ...profileRes.user }));
          }
        } catch (profileError) {
          // تجاهل أي خطأ في التحديث الصامت للبروفايل
          // Profile refresh error ignored
        }
      } catch (error) {
        const errMsg = error.response?.data?.error || 'الرابط غير صالح أو منتهي.';
        setStatus('error');
        setMessage(errMsg);
        toast.error(errMsg);
      }
    };

    verify();
  }, [token, toast, updateUser]);

  const renderIcon = () => {
    if (status === 'loading') {
      return <Mail className="w-10 h-10 text-primary-600 animate-pulse" />;
    }
    if (status === 'success') {
      return <CheckCircle2 className="w-12 h-12 text-emerald-500" />;
    }
    return <XCircle className="w-12 h-12 text-rose-500" />;
  };

  const renderTitle = () => {
    if (status === 'loading') return 'جاري التحقق من رابط التفعيل...';
    if (status === 'success') return 'تم تفعيل بريدك الإلكتروني بنجاح!';
    return 'تعذر تفعيل البريد الإلكتروني';
  };

  return (
    <div className="min-h-screen bg-gray-50/60 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* خلفية جمالية مشابهة لصفحة التسجيل */}
      <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-100/40 rounded-full blur-[120px] -z-10 animate-pulse" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[30%] h-[40%] bg-blue-100/40 rounded-full blur-[100px] -z-10" />

      <div className="max-w-lg mx-auto w-full space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {/* الهيدر والبراند */}
        <div className="text-center space-y-4">
          <Link to="/" className="inline-flex items-center gap-3 group">
            <div className="bg-primary-600 p-4 rounded-[1.5rem] shadow-xl shadow-primary-200 transform group-hover:rotate-12 transition-all duration-500">
              <GraduationCap className="h-8 w-8 text-white" />
            </div>
            <div className="text-right">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none">مـنـصـتـي</h1>
              <p className="text-primary-600 text-xs font-black uppercase tracking-[0.2em] mt-1">
                تفعيل البريد الإلكتروني
              </p>
            </div>
          </Link>
        </div>

        {/* الكارد الرئيسية */}
        <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 p-8 lg:p-10 border border-gray-100 text-center space-y-6">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-3xl bg-gray-50 flex items-center justify-center shadow-inner mb-2">
              {status === 'loading' ? <LoadingSpinner size="medium" /> : renderIcon()}
            </div>
            <h2 className="text-xl font-black text-gray-900">{renderTitle()}</h2>
            <p className="text-gray-500 text-sm font-medium leading-relaxed">
              {status === 'loading'
                ? 'برجاء الانتظار لثوانٍ قليلة حتى ننتهي من تأكيد بريدك الإلكتروني.'
                : message}
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-3 rounded-2xl bg-primary-600 text-white text-sm font-black shadow-lg shadow-primary-200 hover:bg-primary-700 transition-all"
            >
              تسجيل الدخول إلى المنصة
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-gray-50 text-gray-700 text-sm font-black border border-gray-200 hover:bg-white hover:border-primary-200 transition-all"
            >
              العودة للصفحة الرئيسية
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;

